// src/student/pages/StudentMonthlyCA.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Download,
  BookOpen,
  Search,
  RefreshCw,
  FileText,
} from "lucide-react";
import { studentPortalAPI } from "../services/studentApi";

const MONTHS = [
  "All",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const YEARS = ["All", "2026", "2025", "2024", "2023"];

export default function StudentMonthlyCA() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedYear, setSelectedYear] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const fetchMonthlyCA = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await studentPortalAPI.getMonthlyCA();
      if (res.success && Array.isArray(res.data)) {
        setItems(res.data);
      }
    } catch (err) {
      setError(err.message || "Failed to load Monthly Current Affairs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMonthlyCA();
  }, [fetchMonthlyCA]);

  const resolveUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    const base = (
      process.env.NODE_ENV === "development"
        ? (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL !== "/api"
            ? process.env.REACT_APP_API_URL
            : "http://localhost:5001/api")
        : (process.env.REACT_APP_API_URL || "/api")
    ).replace(/\/api$/, "");
    return `${base}${url.startsWith("/") ? "" : "/"}${url}`;
  };

  const filteredItems = items.filter((item) => {
    const matchesMonth =
      selectedMonth === "All" ||
      (item.month || "").toLowerCase() === selectedMonth.toLowerCase();

    const matchesYear =
      selectedYear === "All" || String(item.year || "") === selectedYear;

    const matchesQuery =
      !searchQuery ||
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description &&
        item.description.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesMonth && matchesYear && matchesQuery;
  });

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-blue-950 via-slate-900 to-[#0b1528] rounded-2xl p-6 text-white shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300 bg-white/10 px-2.5 py-0.5 rounded-full">
              Consolidated Compendiums
            </span>
            <span className="text-xs text-slate-300 font-medium">UPSC CSE</span>
          </div>
          <h1 className="text-xl md:text-2xl font-black tracking-tight">
            Monthly Current Affairs
          </h1>
          <p className="text-xs text-slate-300 mt-1 max-w-xl">
            Monthly digests consolidating editorial debates, government schemes, indices, and prelims facts into comprehensive revision modules.
          </p>
        </div>

        <button
          onClick={fetchMonthlyCA}
          className="self-start md:self-auto flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-xs font-semibold backdrop-blur-xs transition-colors"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-2xs flex flex-col md:flex-row gap-3 items-center">
        <div className="flex-1 w-full relative">
          <Search className="absolute left-3 top-2.5 text-slate-400" size={16} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search monthly compilation title..."
            className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-800 focus:bg-white"
          />
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto">
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="flex-1 md:flex-none px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-800"
          >
            {MONTHS.map((m) => (
              <option key={m} value={m}>
                {m === "All" ? "All Months" : m}
              </option>
            ))}
          </select>

          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="flex-1 md:flex-none px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-800"
          >
            {YEARS.map((y) => (
              <option key={y} value={y}>
                {y === "All" ? "All Years" : y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Compendiums Grid */}
      {loading ? (
        <div className="py-16 text-center text-slate-400 text-sm">
          Loading Monthly Compendiums...
        </div>
      ) : error ? (
        <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700">
          {error}
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="py-16 bg-white rounded-2xl border border-slate-200 text-center">
          <BookOpen className="mx-auto text-slate-300 mb-2" size={36} />
          <p className="text-sm font-semibold text-slate-700">No monthly compendiums found</p>
          <p className="text-xs text-slate-400 mt-1">Try selecting another month or year.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredItems.map((item) => {
            const pdfLink = resolveUrl(item.pdfUrl || item.fileUrl);
            return (
              <div
                key={item._id}
                className="bg-white rounded-2xl border border-slate-200/90 hover:border-blue-800/40 hover:shadow-lg transition-all duration-200 flex flex-col justify-between overflow-hidden group"
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[11px] font-bold text-blue-900 bg-blue-50 border border-blue-100 px-2.5 py-0.5 rounded-full">
                      {item.month} {item.year}
                    </span>
                    <span className="text-[11px] text-slate-400 font-medium">
                      Monthly Digest
                    </span>
                  </div>

                  <h3 className="text-sm font-bold text-slate-900 group-hover:text-blue-900 transition-colors line-clamp-2 mb-2">
                    {item.title}
                  </h3>

                  <p className="text-xs text-slate-600 line-clamp-3 leading-relaxed">
                    {item.description || "Consolidated monthly overview of key current events, government policies, and editorial opinions."}
                  </p>
                </div>

                <div className="bg-slate-50 px-5 py-3 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-[11px] text-slate-500 font-medium flex items-center gap-1">
                    <FileText size={13} /> Official Publication
                  </span>

                  {pdfLink ? (
                    <a
                      href={pdfLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-900 hover:bg-blue-800 text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
                    >
                      <Download size={13} />
                      <span>Download PDF</span>
                    </a>
                  ) : (
                    <span className="text-xs text-slate-400">PDF coming soon</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
